import React, { useState } from 'react';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
import { PACKAGES, COUPONS, PAGE_EDITION_MAPPER, FEATURES, INTEGRATIONS, getOfferPrice } from '../../Constants/Packages';
import ConditionalDiv from '../Common/ConditionalDiv';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { getActiveOffer } from '../../Utils/OfferEngine';
import { FLASHOFFER } from '../../Constants/Const';
import { useBrand } from '../../Utils/BrandContext';

const PackagesSection = ({ onBuyNow, showCoupons = false, pageName = 'GERP', isFirstSection = false, isCpnExpanded = false, onCouponView, onPackageClick, onPackageView, onGetOffer, isFromOfr = false }) => {
  let visibleEditions = [];
  const isMobile = useIsMobile()
  const activeOffer = getActiveOffer()
  const flashOfferOn = activeOffer?.offerKey === 'FLASH_EOD'
  const sundayOfferOn = activeOffer?.offerKey === 'SUNDAY'
  const d3OfferOn = activeOffer?.offerKey === 'D3'
  // Any offer that isn't Flash/Sunday/Fresh — D3, OLD_PL, FDBK_CLRNC, etc.
  const hasGenericOffer = !!activeOffer && !flashOfferOn && !sundayOfferOn && activeOffer?.offerKey !== 'FRESH'
  const { brand: activeBrand } = useBrand()


  if (pageName && PAGE_EDITION_MAPPER[pageName.toLowerCase()]) {
    const pageConfig = PAGE_EDITION_MAPPER[pageName.toLowerCase()];
    visibleEditions = pageConfig.editionIds
      .map(id => PACKAGES[id])
      .filter(edition => edition)
      // .filter(edition => activeBrand === 'qikerp' ? edition.id !== 34 && edition.id !== 35 : edition.id !== 36);
      .filter(edition => pageName?.toLowerCase() == 'gpower' ? edition.id !== 34 && edition.id !== 35 : edition.id !== 36);
  }

  // Modal state
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedEditionForCoupons, setSelectedEditionForCoupons] = useState(null);
  const { showMM, showYY } = { showMM: false, showYY: true };

  const features = FEATURES;
  const integrations = INTEGRATIONS;

  const calculateDiscount = (stdAmt, afterDsc) => {
    if (!stdAmt || stdAmt === 0) return 0;
    return Math.round(((stdAmt - afterDsc) / stdAmt) * 100);
  };

  const getDisplayPrice = (edition) => {
    // Priority order for price display:
    // 1. If flashOfferOn, show afterDsc as main price
    // 2. If showD1Offer, show afterDsc as main price
    // 3. If showStdOffer, show baseAmt as main price
    // 4. Default: show baseAmt as main price

    const hasSpecialOffer = edition.flashOfferOn || edition.showD1Offer || edition.showStdOffer;

    if (edition.flashOfferOn || edition.showD1Offer) {
      return {
        mainPrice: edition.afterDscStr || edition.afterDsc,
        mainPriceAmount: edition.afterDsc,
        strikethroughPrice: edition.stdAmtStr || null,
        strikethroughAmount: edition.stdAmt || null,
        cutPrice: edition.baseAmtStr,
        cutAmount: edition.baseAmt,
        discountPercent: edition.discPercent,
        showStrikethrough: true,
        showCutthrough: true,
        offerLabel: edition.flashSaleName || 'Special Offer'
      };
    } else if (edition.stdAmt && edition.stdAmt > edition.baseAmt) {
      // Show standard price strikethrough with base amount as main
      return {
        mainPrice: edition.baseAmtStr,
        mainPriceAmount: edition.baseAmt,
        strikethroughPrice: edition.stdAmtStr,
        strikethroughAmount: edition.stdAmt,
        cutPrice: null,
        cutAmount: null,
        discountPercent: calculateDiscount(edition.stdAmt, edition.baseAmt),
        showStrikethrough: true,
        showCutthrough: false
      };
    } else {
      // Default: show base amount as main price
      return {
        mainPrice: edition.baseAmtStr,
        mainPriceAmount: edition.baseAmt,
        strikethroughPrice: null,
        strikethroughAmount: null,
        cutPrice: null,
        cutAmount: null,
        discountPercent: 0,
        showStrikethrough: false,
        showCutthrough: false
      };
    }
  };

  const getMonthlyDisplayPrice = (monthlyData) => {
    if (!monthlyData) return null;

    return {
      mainPrice: monthlyData.baseAmtStr || monthlyData.baseAmt,
      mainPriceAmount: monthlyData.baseAmt,
      discountPercent: monthlyData.discPercent || 0
    };
  };

  const renderQuotasDisplay = (quotas) => {
    if (!quotas || quotas.length === 0) {
      return null;
    }
    return quotas.map((quota, idx) => (
      <React.Fragment key={idx}>
        <span dangerouslySetInnerHTML={{ __html: quota }} />
        {idx < quotas.length - 1 && ', '}
      </React.Fragment>
    ));
  };

  const getCouponsByEdition = (editionID) => {
    return Object.values(COUPONS).filter(coupon => coupon.editionID === editionID && coupon.isActive);
  };

  const handleViewCoupons = (edition) => {
    setSelectedEditionForCoupons(edition);
    setShowCouponModal(true);
  };

  const handleUseCoupon = (couponCode, editionId, forcePeriod = null) => {
    onGetOffer()
    // 1. Get lead info from context/storage
    const allDataRaw = localStorage.getItem('allData');
    if (!allDataRaw) return;

    const allData = JSON.parse(allDataRaw);
    const leadInfo = allData.info ? JSON.parse(allData.info) : [];
    const leadO = leadInfo[0]?.LeadO || '';
    const mobile = allData.mobile || '';

    // 2. Determine plan period - forcePeriod overrides coupon lookup (used for flash offer)
    let selectedPeriod = forcePeriod || "YY";
    let isYear2 = forcePeriod === "YY2";

    if (!forcePeriod) {
      const couponDetails = Object.values(COUPONS).find(
        (c) => c.code === couponCode && c.editionID === editionId
      );
      if (couponDetails) {
        selectedPeriod = couponDetails.planYear === 1 ? "YY" : `YY${couponDetails.planYear}`;
        isYear2 = couponDetails.planYear === 2;
      }
    }

    // 3. Set essential payment items
    localStorage.setItem("selEdn", editionId);
    localStorage.setItem("selPrd", selectedPeriod);
    localStorage.setItem(isYear2 ? "cpnYY2" : "cpn", couponCode);
    localStorage.setItem("guidePG", window.location.pathname.substring(1));

    // 4. Ensure cio is present (BZPay requirement)
    const cioData = [{ 'i': mobile, 'o': leadO }];
    localStorage.setItem('cio', JSON.stringify(cioData));

    // 5. Redirect
    window.location.href = "/Html/External/BZPay.html";
  };
  const CouponModal = () => {
    if (!showCouponModal || !selectedEditionForCoupons) return null;

    const coupons = getCouponsByEdition(selectedEditionForCoupons.id);
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ">
        <div className="bg-[#f1f1f0] rounded max-w-sm w-full overflow-y-auto">
          {/* Modal Header */}
          <div className="sticky top-0 p-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-text-primary">Discount Coupons</h2>
            <button
              onClick={() => setShowCouponModal(false)}
              className="text-2xl text-text-secondary hover:text-text-primary"
            >
              ✕
            </button>
          </div>

          {/* Modal Content */}
          <div className="px-4">
            <div className="space-y-2">
              {/* Flash Offer Box - shown only if flashOfferOn */}
              {flashOfferOn && (
                <div className="rounded border border-primary bg-primary-subtle p-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Offer Ending Soon!</h3>
                  <p className="text-sm text-text-secondary mb-1">
                    Get additional Discount
                  </p>
                  <p className="text-sm text-text-primary mb-3">
                    <span
                      className="font-mono font-bold text-primary"
                      style={{ animation: 'blink 1.2s step-start infinite' }}
                    >
                      FLASHOFFER
                    </span>
                  </p>
                  <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
                  <Button
                    onClick={() => handleUseCoupon('FLASHOFFER', selectedEditionForCoupons.id, 'YY2')}
                    size="sm"

                  >
                    <i className="fa fa-tag mr-2"></i> Get Offer
                  </Button>
                </div>
              )}

              {sundayOfferOn && (
                <div className="rounded border border-primary bg-primary-subtle p-4">
                  <h3 className="text-lg font-bold text-primary-600 mb-2">Sunday Special!</h3>
                  <p className="text-sm text-text-secondary mb-1">
                    Extra discount off on your first year
                  </p>
                  <p className="text-sm text-text-primary mb-3">
                    <span className="font-mono font-bold text-primary">
                      SUNSPL30
                    </span>
                  </p>
                  <Button
                    onClick={() => handleUseCoupon('SUNSPL30', selectedEditionForCoupons.id, 'YY')}
                    size="sm"
                  >
                    <i className="fa fa-tag mr-2"></i> Get Offer
                  </Button>
                </div>
              )}

              {/* Regular Coupons */}
              {coupons.length > 0 ? (
                coupons.map((coupon) => (
                  <div key={coupon.id} className="border-2 bg-white border-border rounded p-4">
                    <h3 className="text-lg font-bold text-text-primary mb-2">{coupon.title}</h3>
                    <p className="text-sm text-text-secondary mb-3">
                      {coupon.description} using coupon code: {' '}
                      <span className="font-mono font-bold text-primary">{coupon.code}</span>
                    </p>
                    <Button
                      onClick={() => handleUseCoupon(coupon.code, selectedEditionForCoupons.id)}
                      size="sm"
                    >
                      <i className="fa fa-tag mr-2"></i> Get Offer
                    </Button>
                  </div>
                ))
              ) : (
                !flashOfferOn && (
                  <p className="text-center text-text-secondary py-8">
                    No coupons available for this edition at the moment.
                  </p>
                )
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="p-4 flex justify-start">
            <Button variant="secondary" size="sm" onClick={() => setShowCouponModal(false)}> Close </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderPackageCardMobile = (edition) => {
    const availableCoupons = getCouponsByEdition(edition.id);
    const priceDisplay = getDisplayPrice(edition);
    const offerPrice = getOfferPrice(edition, activeOffer);

    return (
      <div
        key={edition.id}
        className="w-full bg-background-light border border-border rounded p-3 text-left flex flex-col shadow-[0_2px_5px_rgba(0,0,0,0.05)]"
      >
        <h2 className="text-[24px] font-bold mt-0 mb-0 text-text-primary">
          {edition.name}
        </h2>
        <p className="text-[14px] text-text-primary mb-3 italic">
          {edition.tagline || 'Complete business solution for your enterprise'}
        </p>

        {edition.quotas && edition.quotas.length > 0 && (
          <p className="text-sm text-text-primary mb-3">
            Quotas: {renderQuotasDisplay(edition.quotas)}
          </p>
        )}

        {/* Flash Sale or Special Offer Label */}
        {(edition.flashOfferOn || edition.showD1Offer) && (
          <div className="mb-2 bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded text-[12px] font-semibold">
            {priceDisplay.offerLabel}
          </div>
        )}
        {/* MOBILE PRICE BOX — show only one panel: Flash > Sunday > D3 > Standard */}
        {flashOfferOn ? (
          <div className="mb-3 border-2 border-primary bg-primary-subtle rounded p-3">
            <h2 className="m-0 font-bold text-primary text-[19px]">Flash Offer</h2>
            <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">Get second year free</p>
            <div className="flex items-baseline gap-1 flex-wrap mb-3">
              <span className="text-[11px] text-text-secondary">₹</span>
              <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
              <span className="text-[14px] text-text-secondary ml-1">₹</span>
              <span className="text-2xl font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
              <span className="text-xs text-text-secondary">/ year +GST</span>
            </div>
            <Button
              onClick={() => { handleUseCoupon('FLASHOFFER', edition.id, 'YY'); onPackageClick?.(edition.id); }}
              variant="primary"
              className="w-full"
            >
              Get Discount Offer
            </Button>
          </div>
        ) : sundayOfferOn ? (
          <div className="mb-3 border-2 border-primary bg-primary-subtle rounded p-3">
            <h2 className="m-0 font-bold text-primary text-[19px]">Sunday Special</h2>
            <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">Extra {Math.round((1 - Number(offerPrice.displayPrice.replace(/,/g, '')) / Number(offerPrice.stdPrice.replace(/,/g, ''))) * 100)}% off on first year</p>
            <div className="flex items-baseline gap-1 flex-wrap mb-3">
              <span className="text-[11px] text-text-secondary">₹</span>
              <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
              <span className="text-[14px] text-text-secondary ml-1">₹</span>
              <span className="text-2xl font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
              <span className="text-xs text-text-secondary">+GST</span>
            </div>
            <Button
              onClick={() => { handleUseCoupon('SUNSPL30', edition.id, 'YY'); onPackageClick?.(edition.id); }}
              variant="primary"
              className="w-full"
            >
              Get Discount Offer
            </Button>
          </div>
        ) : hasGenericOffer ? (
          <div className="mb-3 border-2 border-primary bg-primary-subtle rounded p-3">
            <h2 className="m-0 font-bold text-primary text-[19px]">Limited Time Offer</h2>
            <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">Extra {Math.round((1 - Number(offerPrice.displayPrice.replace(/,/g, '')) / Number(offerPrice.stdPrice.replace(/,/g, ''))) * 100)}% off — Today Only!</p>
            <div className="flex items-baseline gap-1 flex-wrap mb-3">
              <span className="text-[11px] text-text-secondary">₹</span>
              <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
              <span className="text-[14px] text-text-secondary ml-1">₹</span>
              <span className="text-2xl font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
              <span className="text-xs text-text-secondary">+GST</span>
            </div>
            <Button
              onClick={() => { handleUseCoupon(activeOffer.couponCode, edition.id, activeOffer.planPeriod); onPackageClick?.(edition.id); }}
              variant="primary"
              className="w-full"
            >
              Get Discount Offer
            </Button>
          </div>
        ) : (
          <div className="bg-white border-2 border-border rounded p-3 mb-0">
            {priceDisplay.showStrikethrough && priceDisplay.strikethroughPrice && (
              <div className="mb-2">
                <span className="text-sm text-text-primary">Regular Rate: </span>
                <span className="font-bold text-text-secondary">
                  <span className="text-[11px]">₹ </span>{priceDisplay.strikethroughPrice}
                  <span className="text-[11px]"> / year</span>
                </span>
              </div>
            )}
            {priceDisplay.showCutthrough && priceDisplay.cutPrice && (
              <div className="mb-2">
                <span className="text-sm text-text-primary">Regular Price: </span>
                <span className="font-bold text-text-secondary">
                  <span className="text-[11px]">₹ </span>{priceDisplay.cutPrice}
                  <span className="text-[11px]"> / year</span>
                </span>
              </div>
            )}
            <div className="mb-3">
              <div className="flex items-baseline gap-1 flex-wrap">
                <span className="text-sm text-text-primary">Offer Rate:</span>
                <span className="text-sm text-text-secondary">₹</span>
                <span className="text-2xl font-extrabold text-text-primary">{priceDisplay.mainPrice}</span>
                <span className="text-xs text-text-secondary">/ year</span>
              </div>
              {priceDisplay.discountPercent > 0 && (
                <>
                  <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
                  <span className="text-lg font-bold text-primary" style={{ animation: 'blink 1.2s step-start infinite' }}>{priceDisplay.discountPercent}% OFF!</span>

                </>
              )}
            </div>
            {/* <p className="text-xs text-text-secondary mb-4">(GST extra. Limited time offer.)</p> */}
            <Button
              onClick={() => { onBuyNow(edition.id); onPackageClick?.(edition.id); }}
              variant="primaryDarkBlue"
              className="w-full"
            >
              Get Discount Offer
            </Button>
            {showCoupons && availableCoupons.length > 0 && (
              <Button
                onClick={() => { handleViewCoupons(edition); onCouponView?.(edition.name); }}
                variant="secondaryDarkBlue"
                className="w-full mt-3"
              >
                View Discount Offers
              </Button>
            )}
          </div>
        )}

        {/* View Discount Coupons
        {showCoupons && availableCoupons.length > 0 && (
          <>
            {!isCpnExpanded ? (
              <button
                onClick={() => { handleViewCoupons(edition); onCouponView?.(edition.name); }}
                className="w-full mt-3 bg-primary text-white font-semibold py-2 px-4 rounded border transition-colors flex items-center justify-between"
              >
                <span>View Discount Coupons</span>
                <span>›</span>
              </button>
            ) : (
              <div className="mt-3 space-y-2">
                <h3 className="text-base font-bold text-text-primary mb-2">Available Discount Coupons:</h3>
                {availableCoupons.map((coupon) => (
                  <div key={coupon.id} className="border-2 bg-white border-border rounded p-3">
                    <h4 className="text-sm font-bold text-text-primary mb-1">{coupon.title}</h4>
                    <p className="text-xs text-text-secondary mb-3">
                      {coupon.description} using coupon code:{' '}
                      <span className="font-mono font-bold text-primary">{coupon.code}</span>
                    </p>
                    <Button onClick={() => handleUseCoupon(coupon.code, edition.id)} size="sm">
                      <i className="fa fa-tag mr-2"></i> Get Offer
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )} */}

      </div>
    );
  };

  const renderPackageCard = (edition) => {
    const discountPercent = calculateDiscount(edition.stdAmt, edition.baseAmt);
    const availableCoupons = getCouponsByEdition(edition.id);
    const priceDisplay = getDisplayPrice(edition);
    const offerPrice = getOfferPrice(edition, activeOffer); // stdPrice=strikethrough, displayPrice=offer price
    const monthlyDisplay = edition.monthly ? getMonthlyDisplayPrice(edition.monthly) : null;
    const showBothPrices = showYY && showMM && edition.monthly;

    return (
      <div
        key={edition.id}
        className={`flex-1 min-w-[300px] max-w-[450px] ${isFromOfr ? 'bg-background-light' : 'bg-white'} border border-border rounded p-3 text-left flex flex-col shadow-[0_2px_5px_rgba(0,0,0,0.05)]`}
      >
        <h2 className={`text-[27px] font-bold [text-shadow:1px_1px_1px_rgba(0,0,0,0.1)] mt-0 mb-0 text-text-primary
          }`}>
          {edition.name}
        </h2>
        <p className="text-[15px] text-text-primary mb-4 italic">
          {edition.tagline || 'Complete business solution for your enterprise'}
        </p>

        <div className="text-sm leading-relaxed flex-grow mb-5 text-text-primary">
          <p>All features included</p>
          {edition.quotas && edition.quotas.length > 0 && (
            <p className="mt-4">
              Quotas: {renderQuotasDisplay(edition.quotas)}
            </p>
          )}
          <p className="mt-4">
            Also covered: Customer Support (even on Sundays), Live Training, Desktop+Mobile access
          </p>
        </div>

        {/* Price and Button */}
        <div>
          {/* Flash Sale or Special Offer Label */}
          {(edition.flashOfferOn || edition.showD1Offer) && (
            <div className="mb-2 bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded text-[12px] font-semibold">
              {priceDisplay.offerLabel}
            </div>
          )}

          {/* DESKTOP LAYOUT */}
          {(showYY !== false) && (
            <ConditionalDiv className="hidden md:block mb-3">
              {!flashOfferOn && !sundayOfferOn && !hasGenericOffer &&
                <div className="flex items-end justify-between gap-4 w-full">
                  <div>
                    {priceDisplay.showStrikethrough && priceDisplay.strikethroughPrice && (
                      <div className="mb-2 flex items-baseline gap-2 text-sm text-text-primary">Regular Rate:
                        <span className="text-sm text-text-secondary">
                          <span className='text-[11px]'>₹ </span><span className='font-bold'>{priceDisplay.strikethroughPrice} </span><span className='text-[11px]'>/ year</span>
                        </span>
                      </div>
                    )}
                    {priceDisplay.showCutthrough && priceDisplay.cutPrice && (
                      <div className="text-sm text-text-secondary mb-1">
                        <span className="font-semibold">Regular Price:</span>{' '}
                        <span className="">₹ {priceDisplay.cutPrice}</span>
                      </div>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-lg text-text-secondary">₹</span>
                      <span className="text-[28px] font-extrabold text-text-primary">
                        {priceDisplay.mainPrice}
                      </span>
                      <span className="text-[15px] text-text-secondary">/ year</span>
                      <span className="text-sm text-primary font-bold">
                        ({priceDisplay.discountPercent}% off)
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-end">
                    <Button
                      onClick={() => { onBuyNow(edition.id); onPackageClick?.(edition.id); }}
                      variant="primary"
                      className="w-auto"
                    >
                      Buy Now
                    </Button>
                  </div>
                </div>}

              {/* DESKTOP FLASH OFFER BOX */}
              {flashOfferOn && (
                <div className="border-2 border-primary bg-primary-subtle rounded p-3">
                  <div className="flex items-end justify-between gap-4 w-full">
                    <div>
                      <p className="m-0 font-bold text-primary text-[19px] mb-1">Flash Offer</p>
                      <p className="text-[15px] font-semibold text-text-primary mb-1">{FLASHOFFER.tagline}</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[11px] text-text-secondary">₹</span>
                        <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                        <span className="text-[18px] text-text-secondary ml-1">₹</span>
                        <span className="text-[28px] font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                        <span className="text-[15px] text-text-secondary">+GST</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <Button
                        onClick={() => { handleUseCoupon('FLASHOFFER', edition.id, 'YY'); onPackageClick?.(edition.id); }}
                        variant="primary"
                        className="w-auto"
                      >
                        Buy Now
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* DESKTOP SUNDAY OFFER BOX */}
              {sundayOfferOn && (
                <div className="border-2 border-primary bg-primary-subtle rounded p-3">
                  <div className="flex items-end justify-between gap-4 w-full">
                    <div>
                      <p className="m-0 font-bold text-primary text-[19px] mb-1">Sunday Special</p>
                      <p className="text-[15px] font-semibold text-text-primary mb-1">Extra {Math.round((1 - Number(offerPrice.displayPrice.replace(/,/g, '')) / Number(offerPrice.stdPrice.replace(/,/g, ''))) * 100)}% off on first year</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[11px] text-text-secondary">₹</span>
                        <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                        <span className="text-[18px] text-text-secondary ml-1">₹</span>
                        <span className="text-[28px] font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                        <span className="text-[15px] text-text-secondary">+GST</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <Button
                        onClick={() => { handleUseCoupon('SUNSPL30', edition.id, 'YY'); onPackageClick?.(edition.id); }}
                        variant="primary"
                        className="w-auto"
                      >
                        Buy Now
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* DESKTOP GENERIC OFFER BOX — D3, OLD_PL, FDBK_CLRNC, etc. */}
              {hasGenericOffer && (
                <div className="border-2 border-primary bg-primary-subtle rounded p-3">
                  <div className="flex items-end justify-between gap-4 w-full">
                    <div>
                      <p className="m-0 font-bold text-primary text-[19px] mb-1">Limited Time Offer</p>
                      <p className="text-[15px] font-semibold text-text-primary mb-1">Extra {Math.round((1 - Number(offerPrice.displayPrice.replace(/,/g, '')) / Number(offerPrice.stdPrice.replace(/,/g, ''))) * 100)}% off — Today Only!</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[11px] text-text-secondary">₹</span>
                        <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                        <span className="text-[18px] text-text-secondary ml-1">₹</span>
                        <span className="text-[28px] font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                        <span className="text-[15px] text-text-secondary">+GST</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <Button
                        onClick={() => { handleUseCoupon(activeOffer.couponCode, edition.id, activeOffer.planPeriod); onPackageClick?.(edition.id); }}
                        variant="primary"
                        className="w-auto"
                      >
                        Buy Now
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </ConditionalDiv>
          )}


          {/* MOBILE LAYOUT */}
          {(showYY !== false) && (
            <div className={`md:hidden bg-background-light border-2 border-border rounded p-3 ${flashOfferOn || sundayOfferOn ? 'mb-4' : 'mb-0'}`}>
              {/* Regular Rate - Strikethrough */}
              {priceDisplay.showStrikethrough && priceDisplay.strikethroughPrice && (
                <div className="mb-2">
                  <span className="text-xs text-text-primary">Regular Rate: </span>
                  <span className="text-sm font-bold text-text-secondary"><span className='text-[11px]'>₹ </span>{priceDisplay.strikethroughPrice}<span className='text-[11px]'> / year</span></span>
                </div>
              )}
              {priceDisplay.showCutthrough && priceDisplay.cutPrice && (
                <div className="mb-2">
                  <span className="text-xs text-text-primary">Regular Price: </span>
                  <span className="text-sm font-bold text-text-secondary"><span className='text-[11px]'>₹ </span>{priceDisplay.cutPrice}<span className='text-[11px]'> / year</span></span>
                </div>
              )}

              {/* Offer Rate - Main Price Display */}
              <div className="mb-3">
                <div className="flex items-baseline gap-1 flex-wrap">
                  <span className="text-xs text-text-primary">Offer Rate:</span>
                  <span className="text-sm text-text-secondary">₹</span>
                  <span className="text-2xl font-extrabold text-text-primary">{priceDisplay.mainPrice}</span>
                  <span className="text-xs text-text-secondary">/ year</span>
                  {priceDisplay.discountPercent > 0 && (
                    <span className="text-sm font-bold text-primary">
                      ({priceDisplay.discountPercent}% off)
                    </span>
                  )}
                </div>
              </div>

              <p className="text-xs text-text-secondary mb-4">(GST extra. Limited time offer.)</p>

              {/* Buy Now Button */}
              <Button
                onClick={() => { onBuyNow(edition.id); onPackageClick?.(edition.id); }}
                variant="primary"
                className="w-full"
              >
                Buy Now
              </Button>
            </div>
          )}
          {/* MOBILE FLASH OFFER BOX */}
          {(showYY !== false) && flashOfferOn && (
            <div className="md:hidden mb-3 border-2 border-primary bg-primary-subtle rounded p-3">
              <h2 className="m-0 font-bold text-primary text-[19px] md:text-[28px]">Flash Offer</h2>
              <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">{FLASHOFFER.tagline}</p>
              <div className="flex items-baseline gap-1 flex-wrap mb-3">
                <span className="text-[11px] text-text-secondary">₹</span>
                <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                <span className="text-[14px] text-text-secondary ml-1">₹</span>
                <span className="text-2xl font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                <span className="text-xs text-text-secondary">+GST</span>
              </div>
              <Button
                onClick={() => { handleUseCoupon('FLASHOFFER', edition.id, 'YY'); onPackageClick?.(edition.id); }}
                variant="primary"
                className="w-full"
              >
                Get Discount Offer
              </Button>
            </div>
          )}
          {/* MOBILE SUNDAY OFFER BOX */}
          {(showYY !== false) && sundayOfferOn && (
            <div className="md:hidden mb-3 border-2 border-primary bg-primary-subtle rounded p-3">
              <h2 className="m-0 font-bold text-primary text-[19px] md:text-[28px]">Sunday Special</h2>
              <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">Extra {Math.round((1 - Number(offerPrice.displayPrice.replace(/,/g, '')) / Number(offerPrice.stdPrice.replace(/,/g, ''))) * 100)}% off on first year</p>
              <div className="flex items-baseline gap-1 flex-wrap mb-3">
                <span className="text-[11px] text-text-secondary">₹</span>
                <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                <span className="text-[14px] text-text-secondary ml-1">₹</span>
                <span className="text-2xl font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                <span className="text-xs text-text-secondary">+GST</span>
              </div>
              <Button
                onClick={() => { handleUseCoupon('SUNSPL30', edition.id, 'YY'); onPackageClick?.(edition.id); }}
                variant="primary"
                className="w-full"
              >
                Buy Now
              </Button>
            </div>
          )}


          {/* OR DIVIDER - Show only when both yearly and monthly are displayed */}
          {showBothPrices && (
            <div className="flex items-center gap-2 my-3">
              <div className="flex-1 border-t border-border"></div>
              <span className="text-sm font-semibold text-text-secondary px-2">OR</span>
              <div className="flex-1 border-t border-border"></div>
            </div>
          )}

          {/* MONTHLY PRICING SECTION */}
          {showBothPrices && monthlyDisplay && (
            <div className="mb-4">
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-lg text-text-primary">₹ </span>
                <span className={`text-[28px] font-extrabold text-text-primary
                  }`}>
                  {monthlyDisplay.mainPrice}
                </span>
                <span className="text-[15px]"> / month</span>

                {/* Discount Badge for Monthly */}
                {monthlyDisplay.discountPercent > 0 && (
                  <b className="text-primary text-[15px] ml-1">({monthlyDisplay.discountPercent}% off)</b>
                )}
              </div>
            </div>
          )}

          {/* View Discount Coupons Button or Expanded Coupons */}
          {showCoupons && availableCoupons.length > 0 && (
            <>
              {!isCpnExpanded ? (
                <button
                  onClick={() => { handleViewCoupons(edition); onCouponView?.(edition.name); }}
                  className="w-full mt-3 md:mt-0 bg-primary text-white font-semibold py-2 px-4 rounded border transition-colors flex items-center justify-between"
                >
                  <span>View Discount Coupons</span>
                  <span>›</span>
                </button>
              ) : (
                <div className="mt-3 md:mt-0 space-y-2">
                  <h3 className="text-base font-bold text-text-primary mb-2">Available Discount Coupons:</h3>
                  {availableCoupons.map((coupon) => (
                    <div key={coupon.id} className="border-2 bg-white border-border rounded p-3">
                      <h4 className="text-sm md:text-base font-bold text-text-primary mb-1">{coupon.title}</h4>
                      <p className="text-xs md:text-sm text-text-secondary mb-3">
                        {coupon.description} using coupon code: {' '}
                        <span className="font-mono font-bold text-primary">{coupon.code}</span>
                      </p>
                      <Button
                        onClick={() => handleUseCoupon(coupon.code, edition.id)}
                        size="sm"
                      >
                        <i className="fa fa-tag mr-2"></i> Get Offer
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div >
    );
  };

  const pricingRef = React.useRef(null);
  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { onPackageView?.(); observer.disconnect(); }
    }, { threshold: 0.3 });
    if (pricingRef.current) observer.observe(pricingRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={pricingRef} id="simplePricing" className={`border-top ${!isMobile ? 'bg-background-light' : 'bg-white'} ${isFirstSection && !isMobile ? 'py-6 md:py-8 lg:py-10' : 'py-12 md:py-16 lg:py-20'} text-center `}>
      {/* ========== DESKTOP VIEW ========== */}
      <Container variant="wide" className="hidden md:block">
        {/* Main Heading */}
        <h1 className="text-[40px] font-bold secttl mb-0 px-10 pb-6">
          Packages
        </h1>

        {/* Page-specific description */}
        {pageName && PAGE_EDITION_MAPPER[pageName.toLowerCase()] && (
          <ConditionalDiv className="text-lg text-text-secondary mb-8 max-w-3xl mx-auto">
            {PAGE_EDITION_MAPPER[pageName.toLowerCase()].description}
          </ConditionalDiv>
        )}

        {/* Features Covered */}
        <div className="mb-7 max-w-5xl mx-auto">
          <h4 className="font-bold text-2xl text-text-primary mb-4">
            Each package includes
          </h4>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            {features.map((feature, idx) => (
              <div key={idx} className="text-sm text-text-primary whitespace-nowrap">
                <span className="text-primary font-bold mr-0.5">✓</span>
                {feature}
              </div>
            ))}
          </div>
        </div>

        {/* Integrations */}
        <div className="mb-12 max-w-5xl mx-auto">
          <h4 className="font-bold text-2xl text-text-primary mb-4">
            Integrations
          </h4>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            {integrations.map((integration, idx) => (
              <div key={idx} className="text-sm text-text-primary whitespace-nowrap">
                <span className="text-primary font-bold mr-0.5">✓</span>
                {integration}
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="flex flex-wrap gap-8 justify-center max-w-7xl mx-auto">
          {visibleEditions.map(edition => renderPackageCard(edition))}
        </div>

        <p className="text-[14px] text-text-secondary mt-6">(GST extra. Limited time offer.)</p>

        {/* Footer Note */}
        <div className="mt-10 text-text-primary">
          {visibleEditions.some(e => e.rfdAvl) && (
            <p className="font-bold text-lg">
              Each package is offered with 7-day Moneyback Guarantee
            </p>
          )}
        </div>
      </Container>

      {/* ========== MOBILE VIEW ========== */}
      <Container className="md:hidden">
        {/* Main Heading */}
        <h1 className="text-[30px] font-bold secttl mb-0 px-4 pb-6">
          Unbeatable Pricing
        </h1>

        {/* Page-specific description */}
        {pageName && PAGE_EDITION_MAPPER[pageName.toLowerCase()] && (
          <ConditionalDiv className="text-lg text-text-secondary mb-8 max-w-3xl mx-auto">
            {PAGE_EDITION_MAPPER[pageName.toLowerCase()].description}
          </ConditionalDiv>
        )}

        {/* MOBILE: QikERP brand — single unified card with included list + silver price */}
        {(pageName?.toLowerCase() === 'gpower') ? (
          <>
            <div className="mb-5 text-left px-4">
              <h4 className="font-bold  text-text-primary mb-1">
                What's included:
              </h4>
            </div>
            <div className="mb-5 px-4 text-left">
              {['All above-listed features', 'Free & Fast Support (even on Sundays)', 'Live Training Sessions (Mon-Fri)', 'Videos & PDF materials'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-1.5 text-[14px] text-text-primary mb-1.5">
                  <span className="text-primary font-bold leading-none">✓</span>
                  <span>{item}</span>
                </div>
              ))}
              <div className="flex items-center gap-1.5 text-[14px] text-text-primary mt-3 mb-1.5">
                <span>To manage up to: 10,000 contacts, 10,000 stock items, Single Firm/Company, Multiple Branches/GSTINs.  <br /><b>Unlimited Users allowed!</b></span>
              </div>
            </div>
            {(() => {
              const silverEdition = (pageName?.toLowerCase() === 'gpower')
                ? (visibleEditions.find(e => e.id === 36) || visibleEditions[0])
                : visibleEditions[0];
              if (!silverEdition) return null;
              const priceDisplay = getDisplayPrice(silverEdition);
              const silverOfferPrice = getOfferPrice(silverEdition, activeOffer);
              return (
                <div className="px-4 flex flex-col gap-3">
                  {/* Standard Price Box */}
                  {!flashOfferOn && !sundayOfferOn && !hasGenericOffer && <div className="bg-background-light border-2 border-border rounded p-3 text-left">
                    {priceDisplay.showStrikethrough && priceDisplay.strikethroughPrice && (
                      <div className="mb-2">
                        <span className="text-sm text-text-primary">Regular Rate: </span>
                        <span className="font-bold text-text-secondary">
                          <span className="text-[11px]">₹ </span>{priceDisplay.strikethroughPrice}
                          <span className="text-[11px]"> / year</span>
                        </span>
                      </div>
                    )}
                    <div className="mb-3">
                      <div className="flex items-baseline gap-1 flex-wrap">
                        <span className="text-sm text-text-primary">Offer Rate:</span>
                        <span className="text-sm text-text-secondary">₹</span>
                        <span className="text-2xl font-extrabold text-text-primary">{priceDisplay.mainPrice}</span>
                        <span className="text-xs text-text-secondary">/ year</span>
                      </div>
                      {priceDisplay.discountPercent > 0 && (
                        <>
                          <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
                          <span className="text-lg font-bold text-primary" style={{ animation: 'blink 1.2s step-start infinite' }}>{priceDisplay.discountPercent}% OFF!</span>
                        </>
                      )}
                    </div>
                    <Button
                      onClick={() => { onBuyNow(silverEdition.id); onPackageClick?.(silverEdition.id); }}
                      variant="primaryDarkBlue"
                      className="w-full"
                    >
                      Get Discount Offer
                    </Button>
                  </div>}
                  {/* Common Offer Box — works for all offer types */}
                  {(flashOfferOn || sundayOfferOn || hasGenericOffer) && (
                    <div className="border-2 border-primary bg-primary-subtle rounded p-3 text-left">
                      <h2 className="m-0 font-bold text-primary text-[19px]">{activeOffer?.label}</h2>
                      <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">Extra {Math.round((1 - Number(silverOfferPrice.displayPrice.replace(/,/g, '')) / Number(silverOfferPrice.stdPrice.replace(/,/g, ''))) * 100)}% off on 1st year</p>
                      <div className="flex items-baseline gap-1 flex-wrap mb-3">
                        <span className="text-[11px] text-text-secondary">₹</span>
                        <span className="text-sm font-bold text-text-secondary line-through">{silverOfferPrice.stdPrice}</span>
                        <span className="text-[14px] text-text-secondary ml-1">₹</span>
                        <span className="text-2xl font-extrabold text-text-primary">{silverOfferPrice.displayPrice}</span>
                        <span className="text-xs text-text-secondary">+GST</span>
                      </div>
                      <Button
                        onClick={() => { handleUseCoupon(activeOffer.couponCode, silverEdition.id, activeOffer.planPeriod); onPackageClick?.(silverEdition.id); }}
                        variant="primary"
                        className="w-full"
                      >
                        Get Discount Offer
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        ) : (
          <>
            {/* MOBILE: Non-QikERP brand — original two cards view */}
            <div className="mb-5 text-center px-4">
              <h4 className="font-bold text-xl text-text-primary mb-1">
                Each package includes the features listed above.
              </h4>
            </div>
            <div className="flex flex-col gap-6 justify-center max-w-4xl mx-auto">
              {visibleEditions.map(edition => renderPackageCardMobile(edition))}
            </div>
          </>
        )}
        <p className="text-[14px] text-text-secondary mt-6">(GST extra. Limited time offer!)</p>

        {/* Footer Note */}
        <ConditionalDiv className="mt-8 text-text-primary">
          {visibleEditions.some(e => e.rfdAvl) && (
            <p className="font-bold text-base">
              Each package is offered with 7-day Moneyback Guarantee
            </p>
          )}
        </ConditionalDiv>
      </Container>

      {/* Coupon Modal */}
      <CouponModal />
    </section>
  );
};

export default PackagesSection;